/**
 * projectNavManifest.js — DP278-C6a (June 2026). THE NINE AGREED BANDS.
 *
 * SINGLE SOURCE OF TRUTH for project navigation: the stage rail (PS-1d) and the
 * stage desks render from this file.
 *
 * DP85's six headings grouped by department. These nine read in job order, the
 * way the work actually happens, from the mockup's left column:
 *
 *   PROJECT DESK · 1 PROJECT INFO · 2 PLANS · 3 DESIGN · 4 PRICE · 5 BUILD ·
 *   6 MONEY · TALK · 7 CLOSE
 *
 * Desk and Talk carry no number: one is where you stand, the other runs
 * alongside every band.
 *
 * NOTHING IS REMOVED — Library Hub is on the rail (data/navShellManifest.js),
 * so the column carries 46 of the 47 destinations and the 47th is one tap away
 * from anywhere in the app.
 *
 * An item is exactly one of:
 *   value  — a tab inside the desk (handleTabChange)
 *   route  — a full page (navigate); `:projectId` is substituted at render
 *   action — something the page does (a dialog, a global drawer event)
 */

import {
  Home, Layers, Target, Calculator, FileText, Calendar, DollarSign,
  Users, Folder, Activity, ClipboardList, MessageSquare,
  Palette, Package, CheckSquare, ShieldCheck, Mail, Zap,
  ClipboardCheck, Camera, Receipt,
  BarChart3, Send, Sparkles, PanelLeft, Ruler, HardHat,
  Image, ClipboardEdit, HelpCircle, Scale,
  ScrollText, FileSignature, ShieldAlert, Library, ShoppingCart, Droplets,
  MapPin, Route as RouteIcon, Mic, Eye, DoorClosed,
} from 'lucide-react';

/** Build a route relative to a given project id. Used at render time. */
export const buildRoute = (projectId, template) =>
  (template || '').replace(':projectId', projectId);

/** @type {{id: string, title: string, accent: string, items: object[]}[]} */
export const PROJECT_NAV_SECTIONS = [
  {
    id: 'desk',
    title: 'Project Desk',
    accent: '#6B7F63',
    items: [
      { value: 'project',   label: 'Project Desk',  icon: Home,     testId: 'nav-project-desk' },
    ],
  },
  {
    id: 'setup',
    title: '1 · Project Info',
    accent: '#4A6274',
    items: [
      { value: 'site',      label: 'Site conditions',       icon: MapPin,        testId: 'nav-site-conditions' },
      { value: 'team',     label: 'Team & contacts', icon: Users,         testId: 'nav-team',     helpId: 'hub-team' },
      // SC-6 — the two portals are top-level lines now, with nothing to expand
      // first: the Team Portal IS the Site Office, the Client Portal IS the
      // Drawing Room, and the hover title says so in the firm's own words.
      { value: 'site-office', label: 'Team Portal',   icon: HardHat, testId: 'nav-portal-team',   title: 'Site Office', desk: false },
      { value: 'portals',     label: 'Client Portal', icon: Eye,     testId: 'nav-portal-client', title: 'Drawing Room', helpId: 'hub-portals', desk: false },
      { value: 'journey',   label: 'Journey & phases', icon: RouteIcon, testId: 'nav-journey', helpId: 'hub-journey', desk: false },
      { value: 'lifecycle', label: 'Lifecycle',        icon: Layers,    testId: 'nav-lifecycle', desk: false },
      { value: 'documents', label: 'Documents & files',     icon: Folder,        testId: 'nav-file-control', helpId: 'hub-file-control' },
    ],
  },
  {
    id: 'plans',
    title: '2 · Plans',
    accent: '#4A6274',
    items: [
      { route: '/projects/:projectId/drawings', label: 'Plan Room', icon: PanelLeft, testId: 'nav-plan-room', helpId: 'hub-drawing-control' },
      { route: '/projects/:projectId/floor-plans', label: 'Floor plans & takeoff', icon: Ruler, testId: 'nav-plans-takeoff', helpId: 'hub-plans-takeoff' },
      { route: '/projects/:projectId/scope-builder', label: 'Scope builder', icon: ClipboardEdit, testId: 'nav-scope-builder', helpId: 'hub-scope-builder' },
    ],
  },
  {
    id: 'design',
    title: '3 · Design',
    accent: '#7A5C7D',
    items: [
      { value: 'clips',      label: 'Clips & materials', icon: Layers,   testId: 'nav-clips',      helpId: 'hub-clips' },
      { value: 'moodboards', label: 'Mood boards',       icon: Palette,  testId: 'nav-moodboards', helpId: 'hub-moods' },
      { value: 'paint',      label: 'Paint',             icon: Droplets, testId: 'nav-paint',      helpId: 'hub-paint' },
      { route: '/projects/:projectId/design-companion', label: 'Design Companion', icon: Sparkles, testId: 'nav-design-companion', helpId: 'hub-design-ai', tag: 'AI' },
      { value: 'electrical',     label: 'Panel builder',   icon: Zap,   testId: 'nav-panel-builder', helpId: 'hub-panel-builder' },
      { value: 'preengineering', label: 'Pre-engineering', icon: Ruler, testId: 'nav-pre-engineering', helpId: 'hub-pre-engineering' },
      { route: '/projects/:projectId/decisions', label: 'Decisions', icon: Scale, testId: 'nav-decisions', helpId: 'hub-decisions', framed: true },
    ],
  },
  {
    id: 'price',
    title: '4 · Price',
    accent: '#B07A17',
    items: [
      { value: 'estimating', label: 'Estimates',   icon: Calculator, testId: 'nav-estimates', helpId: 'hub-estimates' },
      { route: '/projects/:projectId/bid-packages', label: 'Bid packages', icon: Package, testId: 'nav-bid-packages', helpId: 'hub-bid-packages' },
      { route: '/projects/:projectId/bids', label: 'Bids & packages', icon: HardHat, testId: 'nav-bids', helpId: 'hub-bids', tag: 'older' },
      { route: '/projects/:projectId/rfps', label: 'RFPs', icon: Send, testId: 'nav-rfps', tag: 'older' },
      { value: 'changeorders', label: 'Change orders', icon: FileText, testId: 'nav-change-orders', helpId: 'hub-change-orders' },
    ],
  },
  {
    id: 'build',
    title: '5 · Build',
    accent: '#8C5A2B',
    items: [
      { value: 'schedule',  label: 'Schedule',      icon: Calendar, testId: 'nav-schedule', helpId: 'hub-schedule' },
      { value: 'progress',  label: 'Site visits',   icon: Camera,   testId: 'nav-site-visits', helpId: 'hub-site-progress' },
      { value: 'site-office', label: 'Site Office',  icon: HardHat,  testId: 'nav-site-office' },
      { route: '/projects/:projectId/gallery', label: 'Photo gallery', icon: Image, testId: 'nav-photo-gallery', framed: true },
      { value: 'punchlist', label: 'Punch list — items', icon: CheckSquare, testId: 'nav-punchlist-items', helpId: 'hub-punchlist' },
      { route: '/projects/:projectId/punch-list', label: 'Punch list — on site', icon: Camera, testId: 'nav-punchlist-onsite' },
      { route: '/projects/:projectId/punch-lists', label: 'Punch lists — by list', icon: ClipboardList, testId: 'nav-punchlist-bylist' },
      { route: '/projects/:projectId/rfis', label: 'RFIs & submittals', icon: HelpCircle, testId: 'nav-rfis', helpId: 'hub-rfis' },
      { route: '/projects/:projectId/purchases', label: 'Purchases', icon: ShoppingCart, testId: 'nav-purchase-orders', helpId: 'hub-purchase-orders', framed: true },
      { value: 'quality-checklists', label: 'Quality checks', icon: ClipboardCheck, testId: 'nav-quality-checklists' },
      { route: '/projects/:projectId/permits', label: 'Permits & inspections', icon: FileSignature, testId: 'nav-permits' },
    ],
  },
  {
    id: 'money',
    title: '6 · Money',
    accent: '#B07A17',
    items: [
      { value: 'invoices',   label: 'Invoices',    icon: Receipt,    testId: 'nav-invoices', helpId: 'hub-invoices' },
      { route: '/projects/:projectId/received-invoices', label: 'Received invoices', icon: ScrollText, testId: 'nav-received-invoices', helpId: 'hub-received-invoices' },
      { route: '/projects/:projectId/draw-requests', label: 'Draw requests', icon: DollarSign, testId: 'nav-draw-requests', helpId: 'hub-draw-requests' },
      { route: '/projects/:projectId/lien-waivers', label: 'Lien waivers', icon: ShieldAlert, testId: 'nav-lien-waivers' },
      { value: 'jobcosting', label: 'Job costing', icon: BarChart3,  testId: 'nav-job-costing', helpId: 'hub-job-costing' },
    ],
  },
  {
    id: 'talk',
    title: 'Talk',
    accent: '#7A5C7D',
    items: [
      { value: 'messages', label: 'Messages',        icon: MessageSquare, testId: 'nav-messages', helpId: 'hub-messages', badge: 'clientMessageCount' },
      { value: 'emails',   label: 'Emails',          icon: Mail,          testId: 'nav-emails',   helpId: 'hub-emails', badge: 'emailCount' },
      { value: 'meetings', label: 'Meetings',        icon: Mic,           testId: 'nav-meetings', helpId: 'hub-meetings' },
      { value: 'actions',   label: 'Actions',       icon: Target,   testId: 'nav-actions',  helpId: 'hub-actions' },
      { value: 'activity',  label: 'Activity',              icon: Activity,      testId: 'nav-activity', helpId: 'hub-activity' },
    ],
  },
  {
    id: 'close',
    title: '7 · Close',
    accent: '#6F6D73',
    items: [
      { route: '/projects/:projectId/warranties', label: 'Warranties', icon: ShieldCheck, testId: 'nav-warranties' },
      { action: 'portfolio-generator', label: 'Portfolio maker', icon: Sparkles, testId: 'nav-portfolio', helpId: 'hub-portfolio' },
      { action: 'close-project', label: 'Close project',    icon: DoorClosed,    testId: 'nav-close-project' },
    ],
  },
];

/** Ids of the nine bands, in order. Used for the collapse state. */
export const PROJECT_NAV_GROUP_IDS = PROJECT_NAV_SECTIONS.map((s) => s.id);

/** Flat list — helpful for keyword lookups and code-review parity checks. */
export const PROJECT_NAV_FLAT = PROJECT_NAV_SECTIONS.flatMap((s) => s.items);

/** Values (tabs) reachable from the navigation. */
export const PROJECT_NAV_TAB_VALUES = PROJECT_NAV_FLAT
  .filter((i) => i.value)
  .map((i) => i.value);

/** Routes (full pages) reachable from the navigation. */
export const PROJECT_NAV_ROUTES = PROJECT_NAV_FLAT
  .filter((i) => i.route)
  .map((i) => i.route);
